import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Apple as AppleIcon,
  Language as LanguageIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useLoading } from '../../contexts/LoadingContext';
import TermsPrivacyModals from '../../components/Modals/TermsPrivacyModals';
import SocialProfileCompletionModal from '../../components/Auth/SocialProfileCompletionModal';
import { useTranslation } from 'react-i18next';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import AuthBackground from '../../components/Auth/AuthBackground';
import { LEGAL_VERSIONS } from '../../utils/constants';
import apiService from '../../services/apiService';



const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, googleLogin, appleLogin, setAuthSession } = useAuth();
  const { setLoading } = useLoading();
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const [registerMode, setRegisterMode] = useState<'options' | 'email' | 'success'>('options');
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuAnchor(event.currentTarget);
  };
  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };
  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    handleLanguageMenuClose();
  };

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    username: '', // Honeypot field
    referralCode: '',
  });

  // Extract 'ref' from URL on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, referralCode: refCode }));
    }
  }, [location.search]);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [shakeTerms, setShakeTerms] = useState(false);
  const [showTermsHint, setShowTermsHint] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [pendingUser, setPendingUser] = useState<any | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  const triggerTermsAttention = () => {
    setShakeTerms(true);
    setTimeout(() => setShakeTerms(false), 600);
    setShowTermsHint(true);
    setTimeout(() => setShowTermsHint(false), 2500);
  };

  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');

  const handleResendEmail = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setResendError('');
    try {
      const response = await apiService.post('/auth/resend-email-verification', {
        email: formData.email.toLowerCase().trim()
      });
      if (response.success) {
        setResendSuccess(true);
      } else {
        setResendError(response.message || 'Failed to resend verification email.');
      }
    } catch (err: any) {
      console.error('Resend verification email error:', err);
      setResendError(err.response?.data?.message || 'Failed to resend verification email.');
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  // Initialize Apple Sign In
  useEffect(() => {
    if ((window as any).AppleID) {
      (window as any).AppleID.auth.init({
        clientId: process.env.REACT_APP_APPLE_CLIENT_ID || 'com.rideclubnet.web',
        scope: 'name email',
        redirectURI: 'https://rideclubnet.com/register',
        usePopup: true,
      });
    }
  }, []);

  const validateForm = (): boolean => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError(t('registerPage.firstNameRequired') || 'First and Last name required.');
      return false;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError(t('registerPage.emailInvalid'));
      return false;
    }
    if (!formData.password || formData.password.length < 8) {
      setError(t('registerPage.passwordLength'));
      return false;
    }
    if (!acceptedTerms) {
      triggerTermsAttention();
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setIsSubmitting(true);
    setLoading(true, t('registerPage.creatingAccount'));

    try {
      const userData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
        username: formData.username,
        referralCode: formData.referralCode.trim(),
        tosVersion: LEGAL_VERSIONS.TOS,
        privacyVersion: LEGAL_VERSIONS.PRIVACY,
      };

      const result = await register(userData);

      if (result.success) {
        setRegisterMode('success');
      } else {
        setError(result.message || t('registerPage.createAccountFailed'));
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setError(error.message || t('registerPage.createAccountFailed'));
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (error) setError('');
  };

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (!acceptedTerms) {
      triggerTermsAttention();
      return;
    }
    if (response.credential) {
      setLoading(true, t('loginPage.signingInGoogle') || 'Signing in with Google...');
      try {
        const result = await googleLogin(response.credential);
        if (result.success) {
          if (result.isNewUser) {
            setPendingToken(result.pendingToken || null);
            setPendingUser(result.pendingUser || null);
            // Show profile completion modal for new users
            setShowProfileCompletion(true);
          } else {
            const searchParams = new URLSearchParams(location.search);
            const redirectPath = searchParams.get('redirect');
            if (redirectPath) {
              navigate(decodeURIComponent(redirectPath));
            } else {
              navigate('/');
            }
          }
        } else {
          setError(result.message || t('loginPage.googleLoginFailed') || 'Google login failed');
        }
      } catch (err: any) {
        setError(t('loginPage.googleLoginFailed') || 'Google login failed');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAppleLogin = async () => {
    if (!acceptedTerms) {
      triggerTermsAttention();
      return;
    }
    try {
      if (!(window as any).AppleID) {
        setError(t('loginPage.appleSdkNotLoaded') || 'Apple Sign In is not available at the moment.');
        return;
      }

      const response = await (window as any).AppleID.auth.signIn();
      if (response.authorization) {
        const { id_token } = response.authorization;
        const appleUser = response.user;

        setLoading(true, t('loginPage.signingInApple') || 'Signing in with Apple...');
        const result = await appleLogin(id_token, appleUser ? {
          givenName: appleUser.name.firstName,
          familyName: appleUser.name.lastName
        } : undefined);

        if (result.success) {
          if (result.isNewUser) {
            setPendingToken(result.pendingToken || null);
            setPendingUser(result.pendingUser || null);
            setShowProfileCompletion(true);
          } else {
            navigate('/');
          }
        } else {
          setError(result.message || 'Apple login failed');
        }
      }
    } catch (appleError) {
      console.error('Apple Login error:', appleError);
      setError(t('loginPage.appleLoginFailed') || 'Apple login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthBackground marqueeText="JOIN THE CLUB // RIDE YOUR WAY // NO LIMITS // JUST VIBES //">
      <Box sx={{ width: '100%', maxWidth: 480, position: 'relative' }}>

        <Box sx={{ position: 'absolute', top: -48, right: 0, zIndex: 10 }}>
          <Tooltip title={t('navbar.language', 'Language')}>
            <IconButton
              onClick={handleLanguageMenuOpen}
              sx={{
                color: theme.palette.text.secondary,
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '20px',
                px: 1.5,
                py: 0.5,
                gap: 1,
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                }
              }}
            >
              <LanguageIcon sx={{ fontSize: '1.25rem' }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                {i18n.language.startsWith('en') ? 'EN' : 
                 i18n.language.startsWith('fr') ? 'FR' : 
                 i18n.language.startsWith('es') ? 'ES' : 
                 i18n.language.startsWith('fa') ? 'FA' : 
                 i18n.language.startsWith('ar') ? 'AR' :
                 i18n.language.startsWith('zh-CN') ? '简' :
                 i18n.language.startsWith('zh-TW') ? '繁' :
                 i18n.language.startsWith('pa') ? 'PA' :
                 i18n.language.startsWith('hi') ? 'HI' : 'EN'}
              </Typography>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={languageMenuAnchor}
            open={Boolean(languageMenuAnchor)}
            onClose={handleLanguageMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 120,
                borderRadius: 2,
                boxShadow: theme.shadows[4],
              }
            }}
          >
            <MenuItem onClick={() => changeLanguage('en')} selected={i18n.language.startsWith('en')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                EN
              </Box> English
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('fr')} selected={i18n.language.startsWith('fr')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                FR
              </Box> Français
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('es')} selected={i18n.language.startsWith('es')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ES
              </Box> Español
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('fa')} selected={i18n.language.startsWith('fa')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                FA
              </Box> فارسی
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('ar')} selected={i18n.language.startsWith('ar')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                AR
              </Box> العربية
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('zh-CN')} selected={i18n.language.startsWith('zh-CN')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                简
              </Box> 简体中文
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('zh-TW')} selected={i18n.language.startsWith('zh-TW')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                繁
              </Box> 繁體中文
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('pa')} selected={i18n.language.startsWith('pa')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                PA
              </Box> ਪੰਜਾਬੀ
            </MenuItem>
            <MenuItem onClick={() => changeLanguage('hi')} selected={i18n.language.startsWith('hi')} sx={{ gap: 1 }}>
              <Box 
                component="span" 
                sx={{ 
                  fontSize: '0.8rem', 
                  fontWeight: 700, 
                  color: 'text.secondary', 
                  bgcolor: 'rgba(0,0,0,0.06)', 
                  borderRadius: '6px', 
                  px: 1.2, 
                  py: 0.4,
                  minWidth: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                HI
              </Box> हिन्दी
            </MenuItem>
          </Menu>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, letterSpacing: '-0.02em', fontSize: '2rem' }}>
            {t('registerPage.join') || 'Create Account'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
            {t('registerPage.createAccountJourney') || 'Join the club today'}
          </Typography>
        </Box>

        {error && (
          <Box ref={errorRef}>
            <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>{error}</Alert>
          </Box>
        )}

        {/* VIEWS */}

        {/* 1. INITIAL OPTIONS VIEW */}
        {registerMode === 'options' && (
          <React.Fragment>
            <Stack spacing={2} sx={{ mb: 4, alignItems: 'center' }}>
              <Box
                sx={{
                  width: '70%',
                  mb: 1,
                  '@keyframes shake': {
                    '0%, 100%': { transform: 'translateX(0)' },
                    '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-5px)' },
                    '20%, 40%, 60%, 80%': { transform: 'translateX(5px)' },
                  },
                  '@keyframes borderPulse': {
                    '0%':   { boxShadow: '0 0 0 0px rgba(255, 167, 38, 0.5)' },
                    '50%':  { boxShadow: '0 0 0 5px rgba(255, 167, 38, 0)' },
                    '100%': { boxShadow: '0 0 0 0px rgba(255, 167, 38, 0)' },
                  },
                  '@keyframes hintFadeIn': {
                    '0%':   { opacity: 0, transform: 'translateY(-4px)' },
                    '15%':  { opacity: 1, transform: 'translateY(0)' },
                    '75%':  { opacity: 1, transform: 'translateY(0)' },
                    '100%': { opacity: 0, transform: 'translateY(-4px)' },
                  },
                  animation: shakeTerms ? 'shake 0.6s ease-in-out' : 'none',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptedTerms}
                      onChange={(e) => {
                        setAcceptedTerms(e.target.checked);
                        if (e.target.checked) {
                          setShowConsentError(false);
                          setError('');
                        }
                      }}
                      sx={{
                        color: shakeTerms ? theme.palette.warning.main : theme.palette.text.secondary,
                        transition: 'color 0.3s',
                        '&.Mui-checked': { color: theme.palette.mode === 'dark' ? '#FFF' : '#000' },
                        '& .MuiSvgIcon-root': shakeTerms ? {
                          animation: 'borderPulse 0.6s ease-out',
                          borderRadius: '3px',
                        } : {},
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      color={shakeTerms ? 'warning.main' : 'text.secondary'}
                      sx={{ fontSize: '0.9rem', lineHeight: 1.5, transition: 'color 0.3s' }}
                    >
                      {t('registerPage.ageConfirmation', 'I confirm that I am legally recognized as an adult where I live and agree to Ride Club\'s')}{' '}
                      <Link
                        component="button"
                        onClick={(e: React.MouseEvent) => { e.preventDefault(); setTermsModalOpen(true); }}
                        sx={{ color: shakeTerms ? 'warning.main' : theme.palette.text.primary, fontWeight: 600, textDecoration: 'none' }}
                      >
                        {t('common.terms', 'Terms of Service')}
                      </Link>{' '}
                      {t('common.and', 'and')}{' '}
                      <Link
                        component="button"
                        onClick={(e: React.MouseEvent) => { e.preventDefault(); setPrivacyModalOpen(true); }}
                        sx={{ color: shakeTerms ? 'warning.main' : theme.palette.text.primary, fontWeight: 600, textDecoration: 'none' }}
                      >
                        {t('common.privacy', 'Privacy Policy')}
                      </Link>
                      {'.'}
                    </Typography>
                  }
                />
                {showTermsHint && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      ml: 1.5,
                      mb: 0.5,
                      color: theme.palette.warning.main,
                      fontWeight: 500,
                      animation: 'hintFadeIn 2.5s ease forwards',
                    }}
                  >
                    ☝️ {t('registerPage.termsRequired') || 'Please agree to continue'}
                  </Typography>
                )}
              </Box>

              <Button
                variant="contained"
                startIcon={<AppleIcon sx={{ fontSize: '1.5rem !important', color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF' }} />}
                sx={{
                  width: '70%',
                  bgcolor: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                  color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
                  borderRadius: '8px',
                  py: 1.2,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: theme.shadows[2],
                  '&:hover': { bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#222222', boxShadow: theme.shadows[4] }
                }}
                onClick={handleAppleLogin}
              >
                {t('registerPage.signUpWithApple', 'Sign up with Apple')}
              </Button>

              <Box sx={{ position: 'relative', width: '70%' }}>
                {/* Hidden Google SDK button */}
                <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, zIndex: 1, overflow: 'hidden' }}>
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError('Google Sign Up Failed')}
                    shape="rectangular"
                    size="large"
                    text="signup_with"
                    width={340} // 70% of 480 (maxWidth) is approx 336
                  />
                </Box>

                {/* Terms intercept overlay */}
                {!acceptedTerms && (
                  <Box
                    onClick={triggerTermsAttention}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      zIndex: 2,
                      cursor: 'pointer',
                      borderRadius: '8px',
                    }}
                  />
                )}

                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    bgcolor: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                    color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
                    borderRadius: '8px',
                    py: 1.2,
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: theme.shadows[2],
                    '&:hover': { bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#222222' },
                    pointerEvents: 'none'
                  }}
                >
                  <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" style={{ width: 18, height: 18, marginRight: 10 }} />
                  {t('registerPage.signUpWithGoogle', 'Sign up with Google')}
                </Button>
              </Box>

              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={() => setRegisterMode('email')}
                sx={{
                  width: '70%',
                  color: theme.palette.text.primary,
                  borderColor: theme.palette.divider,
                  borderRadius: '8px',
                  py: 1.2,
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': { borderColor: theme.palette.text.primary, bgcolor: theme.palette.action.hover }
                }}
              >
                {t('registerPage.signUpWithEmail', 'Sign up with Email')}
              </Button>
            </Stack>


          </React.Fragment>
        )}

        {/* 2. EMAIL FORM VIEW */}
        {registerMode === 'email' && (
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            <div style={{ display: 'none', position: 'absolute', left: '-9999px' }} aria-hidden="true">
              <TextField id="username" name="username" autoComplete="off" tabIndex={-1} value={formData.username} onChange={handleInputChange('username')} />
            </div>

            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="overline" sx={{ color: 'text.secondary', ml: 1, fontWeight: 600, letterSpacing: 1 }}>
                  {t('registerPage.firstNameLabel') || "FIRST NAME"}
                </Typography>
                <TextField
                  fullWidth
                  id="firstName"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={handleInputChange('firstName')}
                  InputProps={{ sx: { fontSize: '1.1rem', padding: '6px 14px' } }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="overline" sx={{ color: 'text.secondary', ml: 1, fontWeight: 600, letterSpacing: 1 }}>
                  {t('registerPage.lastNameLabel') || "LAST NAME"}
                </Typography>
                <TextField
                  fullWidth
                  id="lastName"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={handleInputChange('lastName')}
                  InputProps={{ sx: { fontSize: '1.1rem', padding: '6px 14px' } }}
                />
              </Box>
            </Stack>

            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', ml: 1, fontWeight: 600, letterSpacing: 1 }}>
                {t('registerPage.emailLabel')}
              </Typography>
              <TextField
                fullWidth
                id="email"
                type="email"
                placeholder="name@example.com"
                value={formData.email}
                onChange={handleInputChange('email')}
                autoComplete="email"
                InputProps={{ sx: { fontSize: '1.1rem', padding: '6px 14px' } }}
              />
            </Box>

            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', ml: 1, fontWeight: 600, letterSpacing: 1 }}>
                {t('registerPage.passwordLabel')}
              </Typography>
              <TextField
                fullWidth
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={formData.password}
                onChange={handleInputChange('password')}
                InputProps={{
                  sx: { fontSize: '1.1rem', padding: '6px 14px' },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', ml: 1, fontWeight: 600, letterSpacing: 1 }}>
                {t('registerPage.referralCodeLabel', 'REFERRAL CODE (OPTIONAL)')}
              </Typography>
              <TextField
                fullWidth
                id="referralCode"
                placeholder="e.g. JO-A1B2C3"
                value={formData.referralCode}
                onChange={handleInputChange('referralCode')}
                InputProps={{ sx: { fontSize: '1.1rem', padding: '6px 14px' } }}
              />
            </Box>

            <Box
              sx={{
                '@keyframes shake': {
                  '0%, 100%': { transform: 'translateX(0)' },
                  '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-5px)' },
                  '20%, 40%, 60%, 80%': { transform: 'translateX(5px)' },
                },
                '@keyframes borderPulse': {
                  '0%':   { boxShadow: '0 0 0 0px rgba(255, 167, 38, 0.5)' },
                  '50%':  { boxShadow: '0 0 0 5px rgba(255, 167, 38, 0)' },
                  '100%': { boxShadow: '0 0 0 0px rgba(255, 167, 38, 0)' },
                },
                '@keyframes hintFadeIn': {
                  '0%':   { opacity: 0, transform: 'translateY(-4px)' },
                  '15%':  { opacity: 1, transform: 'translateY(0)' },
                  '75%':  { opacity: 1, transform: 'translateY(0)' },
                  '100%': { opacity: 0, transform: 'translateY(-4px)' },
                },
                animation: shakeTerms ? 'shake 0.6s ease-in-out' : 'none',
                mt: 1,
                mb: 1
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    sx={{
                      color: shakeTerms ? theme.palette.warning.main : theme.palette.text.secondary,
                      transition: 'color 0.3s',
                      '&.Mui-checked': { color: theme.palette.mode === 'dark' ? '#FFF' : '#000' },
                      '& .MuiSvgIcon-root': shakeTerms ? {
                        animation: 'borderPulse 0.6s ease-out',
                        borderRadius: '3px',
                      } : {},
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    color={shakeTerms ? 'warning.main' : 'text.secondary'}
                    sx={{ fontSize: '0.9rem', lineHeight: 1.5, transition: 'color 0.3s' }}
                  >
                    {t('registerPage.ageConfirmation', 'I confirm that I am legally recognized as an adult where I live and agree to Ride Club\'s')}{' '}
                    <Link
                      component="button"
                      type="button"
                      onClick={(e: React.MouseEvent) => { e.preventDefault(); setTermsModalOpen(true); }}
                      sx={{ color: shakeTerms ? 'warning.main' : theme.palette.text.primary, fontWeight: 600, textDecoration: 'none' }}
                    >
                      {t('common.terms', 'Terms of Service')}
                    </Link>{' '}
                    {t('common.and', 'and')}{' '}
                    <Link
                      component="button"
                      type="button"
                      onClick={(e: React.MouseEvent) => { e.preventDefault(); setPrivacyModalOpen(true); }}
                      sx={{ color: shakeTerms ? 'warning.main' : theme.palette.text.primary, fontWeight: 600, textDecoration: 'none' }}
                    >
                      {t('common.privacy', 'Privacy Policy')}
                    </Link>
                    {'.'}
                  </Typography>
                }
              />
              {showTermsHint && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    ml: 1.5,
                    color: theme.palette.warning.main,
                    fontWeight: 500,
                    animation: 'hintFadeIn 2.5s ease forwards',
                  }}
                >
                  ☝️ {t('registerPage.termsRequired') || 'Please agree to continue'}
                </Typography>
              )}
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isSubmitting}
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
                marginTop: 1,
                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#333333' }
              }}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'CREATE ACCOUNT'}
            </Button>

            <Button
              fullWidth
              onClick={() => setRegisterMode('options')}
              sx={{ color: 'text.secondary', fontWeight: 500, mt: 1 }}
            >
              {t('registerPage.alternativeOptions', 'Alternative options')}
            </Button>
          </Box>
        )}

        {/* 3. VERIFICATION MODAL */}
        <Dialog
          open={registerMode === 'success'}
          onClose={() => { }} // Prevent closing by clicking outside
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 2,
              maxWidth: 400,
              bgcolor: theme.palette.background.paper,
              color: theme.palette.text.primary
            }
          }}
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <EmailIcon sx={{ fontSize: 56, color: theme.palette.primary.main }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('registerPage.verifyEmailModal.title') || 'Check Your Email'}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              {(t('registerPage.verifyEmailModal.subtitle') || "We've sent a verification link to {email}.").replace('{email}', formData.email)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('registerPage.verifyEmailModal.description') || 'Please click the link in the email to verify your account. You need to verify your email before you can log in.'}
            </Typography>

            <Box sx={{ p: 2, bgcolor: theme.palette.action.hover, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('registerPage.verifyEmailModal.didntReceive') || "Didn't receive the email?"}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                {t('registerPage.verifyEmailModal.checkSpam') || 'Check your spam folder or try resending.'}
              </Typography>
              {resendSuccess ? (
                <Alert severity="success" sx={{ py: 0.5, px: 1, borderRadius: 1.5, fontSize: '0.85rem' }}>
                  {t('registerPage.verifyEmailModal.resendSuccess', 'Verification email sent successfully!')}
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {resendError && (
                    <Alert severity="error" sx={{ py: 0.5, px: 1, borderRadius: 1.5, fontSize: '0.85rem' }}>
                      {resendError}
                    </Alert>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleResendEmail}
                    disabled={resendLoading}
                    sx={{
                      fontSize: '0.8rem',
                      py: 0.5,
                      textTransform: 'none',
                      borderRadius: 1.5,
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': {
                        borderColor: theme.palette.text.primary,
                        bgcolor: theme.palette.action.selected
                      }
                    }}
                  >
                    {resendLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      t('registerPage.verifyEmailModal.resendButton', 'Resend verification email')
                    )}
                  </Button>
                </Stack>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 2, px: 3 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
                py: 1.5,
                fontWeight: 600,
                borderRadius: 2,
                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? '#f5f5f5' : '#333333' }
              }}
            >
              {t('registerPage.verifyEmailModal.goToLogin') || 'Go to Login'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Sticky Footer */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
            {t('registerPage.alreadyHaveAccount') || 'Already have an account?'}{' '}
            <Link
              component={RouterLink}
              to="/login"
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {t('registerPage.signIn') || 'Sign In'}
            </Link>
          </Typography>
        </Box>

        <TermsPrivacyModals
          termsOpen={termsModalOpen}
          privacyOpen={privacyModalOpen}
          onTermsClose={() => setTermsModalOpen(false)}
          onPrivacyClose={() => setPrivacyModalOpen(false)}
        />

        {/* Social Profile Completion Modal — shown to brand-new Google/Apple users */}
        <SocialProfileCompletionModal
          open={showProfileCompletion}
          onComplete={(updatedUser) => {
            setShowProfileCompletion(false);
            if (pendingToken) {
              setAuthSession(pendingToken, updatedUser);
            }
            const searchParams = new URLSearchParams(location.search);
            const redirectPath = searchParams.get('redirect');
            navigate(redirectPath ? decodeURIComponent(redirectPath) : '/');
          }}
        />

      </Box>
    </AuthBackground>
  );
};

export default RegisterPage;